import { useEffect, useRef, useState } from 'react'

// Keeps the loader visible for at least this long once shown, so a fast
// optimistic move (cache patches instantly) still reads as value → loader →
// new value instead of a flicker.
export const PENDING_LOADER_MIN_VISIBLE_MS = 400

/** Crossfade between an icon and its loader. */
export const ICON_SWAP_TRANSITION = { duration: 0.15 }

/**
 * Tracks `isPending` but holds the "show the loader" flag true for at least
 * {@link PENDING_LOADER_MIN_VISIBLE_MS} after it first turns true, even if
 * the mutation settles sooner.
 */
export function usePendingWithMinimumVisibleDuration(isPending: boolean): boolean {
  const [showLoader, setShowLoader] = useState(false)
  const shownAtRef = useRef<number | null>(null)

  useEffect(() => {
    if (isPending) {
      shownAtRef.current = Date.now()
      setShowLoader(true)
      return
    }
    const shownAt = shownAtRef.current
    if (shownAt === null) return
    const remaining = Math.max(PENDING_LOADER_MIN_VISIBLE_MS - (Date.now() - shownAt), 0)
    const timer = setTimeout(() => {
      shownAtRef.current = null
      setShowLoader(false)
    }, remaining)
    return () => clearTimeout(timer)
  }, [isPending])

  return showLoader
}
